/* eslint-disable */
import {PermissionsAndroid, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Geolocation from 'react-native-geolocation-service';

// All location code lives in this one file. It works on Android, iOS and web:
//   Android: asks via PermissionsAndroid
//   iOS:     asks via Geolocation.requestAuthorization (system popup)
//   web:     the browser shows its own popup when we read the position

export const LOCATION_SERVICES_OFF_MESSAGE =
  'Location services are turned off on this phone. Turn them on in Settings, or choose your city.';

// Can we send the user to the app's system settings? (not possible on web)
export const canOpenSettings = Platform.OS !== 'web';

const webHasGeolocation = () =>
  typeof navigator !== 'undefined' && !!navigator.geolocation;

// ---- iOS: remembering that location was already allowed ----
// Android can ask "is it allowed?" without showing anything
// (PermissionsAndroid.check). On iOS this library has no such silent check:
// the only call, requestAuthorization, shows the popup the first time.
// So after the user allows location once we remember it here, and the next
// time the screen can skip the "Allow location" step. When the permission is
// already decided, iOS never shows the popup again: requestAuthorization just
// answers with the current state (so if the user later turned it off in
// Settings, we find out without any popup).
const GRANTED_KEY = 'locationGrantedOnce';

const rememberGranted = async granted => {
  try {
    await AsyncStorage.setItem(GRANTED_KEY, granted ? '1' : '0');
  } catch (e) {}
};

// Is location already allowed? Never shows a popup.
export const hasLocationPermission = async () => {
  try {
    if (Platform.OS === 'android') {
      return await PermissionsAndroid.check(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );
    }
    if (Platform.OS === 'web') {
      if (navigator.permissions?.query) {
        const res = await navigator.permissions.query({name: 'geolocation'});
        return res.state === 'granted';
      }
      return false;
    }
    // iOS
    return (await AsyncStorage.getItem(GRANTED_KEY)) === '1';
  } catch (e) {
    return false;
  }
};

// Resolves to: 'granted' | 'denied' | 'blocked' | 'disabled'
//   denied   = user said no, we can ask again (Android)
//   blocked  = refused for good: must be changed in Settings / browser site settings
//   disabled = location services are switched off on the phone (iOS)
export const requestLocationPermission = async () => {
  if (Platform.OS === 'android') {
    const result = await PermissionsAndroid.request(
      PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      {
        title: 'Location access',
        message: 'Pompi uses your location to find the cheapest pumps near you.',
        buttonPositive: 'Allow',
        buttonNegative: 'Not now',
      },
    );
    if (result === PermissionsAndroid.RESULTS.GRANTED) return 'granted';
    if (result === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) return 'blocked';
    return 'denied';
  }

  if (Platform.OS === 'web') {
    // Browsers have no "request permission" call. The popup appears when we read
    // the position. We only check here whether the user already blocked it.
    try {
      if (navigator.permissions?.query) {
        const res = await navigator.permissions.query({name: 'geolocation'});
        if (res.state === 'denied') return 'blocked';
      }
    } catch (e) {
      // Some browsers don't support the Permissions API: just try anyway.
    }
    return 'granted';
  }

  // iOS: shows the system popup only the first time. After that it just
  // returns the current state. A refusal can only be undone in Settings.
  try {
    const status = await Geolocation.requestAuthorization('whenInUse');
    if (status === 'granted') {
      await rememberGranted(true);
      return 'granted';
    }
    await rememberGranted(false);
    if (status === 'disabled') return 'disabled';
    return 'blocked'; // 'denied' or 'restricted'
  } catch (e) {
    return 'denied';
  }
};

// Resolves to {latitude, longitude, accuracy}. Rejects with an error that has
// a numeric `code`: 1 = permission denied, 2 = unavailable, 3 = timeout,
// 5 = location settings not satisfied.
export const getCurrentLocation = () =>
  new Promise((resolve, reject) => {
    if (Platform.OS === 'web' && !webHasGeolocation()) {
      // Without this check the library would never call back and we'd hang.
      reject({code: 2});
      return;
    }
    Geolocation.getCurrentPosition(
      pos => {
        if (Platform.OS === 'ios') {
          rememberGranted(true); // reading a position worked, so it is allowed
        }
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      err => reject(err),
      {
        // We only need the city, so a rough, recent position is enough. High
        // accuracy waits for a GPS fix, which can take many seconds.
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 60000,
        showLocationDialog: true, // Android: offer to switch GPS on
        forceRequestLocation: true,
      },
    );
  });

export const locationErrorMessage = err => {
  switch (err?.code) {
    case 1:
      return 'Location permission is off for this app.';
    case 2:
    case 5:
      return 'Could not get your position. Make sure GPS / location is turned on.';
    case 3:
      return 'Finding your location took too long. Please try again.';
    default:
      return 'Something went wrong while finding your location.';
  }
};